// Trusted CDN domains — bypass attachment validation preflight.
// Includes Meta/Instagram CDNs and our own storage (WAPI inbound media
// is proxied through leadsales.services/<env>/<workspace>/drive/...).
const TRUSTED_CDN_DOMAINS = [
  'fbsbx.com',
  'fbcdn.net',
  'facebook.net',
  'fbpigeon.com',
  'cdninstagram.com',
  'leadsales.services',
];

const SOCIAL_MEDIA_DOMAINS = [
  'facebook.com',
  'instagram.com',
  'twitter.com',
  'x.com',
  'tiktok.com',
  'linkedin.com',
  'youtube.com',
  'pinterest.com',
];

/**
 * True when `hostname` is one of `domains` or a subdomain of one.
 *
 * Suffix match against an already-parsed hostname — never a substring test on
 * the raw URL, so `notfacebook.com` does not match `facebook.com` while
 * `m.facebook.com` does.
 */
export function hostnameMatchesDomain(
  hostname: string,
  domains: string[],
): boolean {
  return domains.some(
    (domain) => hostname === domain || hostname.endsWith(`.${domain}`),
  );
}

/**
 * Prepends `https://` to bare domains so the URL is parseable by downstream services.
 * Returns the original value unchanged if it already has a protocol or is empty.
 */
export function normalizeUrl(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/**
 * Returns true when the URL points to a trusted media host
 * (Meta/Instagram CDNs or Leadsales storage proxy).
 * Used to bypass browser element validation for trusted attachments.
 */
export function isTrustedCdnUrl(url: string): boolean {
  try {
    const { hostname, protocol } = new URL(url);
    if (protocol !== 'https:') return false;
    return hostnameMatchesDomain(hostname, TRUSTED_CDN_DOMAINS);
  } catch {
    return false;
  }
}

// Leadsales' own S3-backed storage/CDN host (prod `leadsales.services`, staging
// `*.leadsales.services`). Unlike Meta CDN URLs — which are signed and expire —
// objects here are permanent, so an <img> load error on one is a format/decode
// problem, not expiry. Also enforces https, so it doubles as the safe-URL guard
// for rendering an attachment URL into an <a href> sink.
const LEADSALES_STORAGE_HOST = 'leadsales.services';

export function isLeadsalesStorageUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const { hostname, protocol } = new URL(url);
    if (protocol !== 'https:') return false;
    return (
      hostname === LEADSALES_STORAGE_HOST ||
      hostname.endsWith(`.${LEADSALES_STORAGE_HOST}`)
    );
  } catch {
    return false;
  }
}

// The bucket's own host. The mobile app and older rows store it instead of the
// CDN host; both name the same object.
const LEADSALES_STORAGE_BUCKET_HOST = 'leadsales-storage.s3.amazonaws.com';
const STORAGE_DRIVE_SEGMENT = '/drive/';

/**
 * BUG-912: the storage id of the workspace file a URL points at, or undefined
 * when the URL is not a file of Leadsales' storage. The id is what follows
 * `/drive/` in `https://leadsales.services/<env>/<workspace>/drive/<fileID>`,
 * decoded: the same id Ajustes → Almacenamiento lists and deletes files by.
 *
 * Quick replies and Leadbot flows keep the absolute URL of each attachment, so
 * this is how a stored attachment is matched to the file behind it.
 */
export function storageFileIdFromUrl(
  url: string | null | undefined,
): string | undefined {
  if (!url) return undefined;

  let pathname: string;
  try {
    const parsed = new URL(url);
    const isBucketHost =
      parsed.protocol === 'https:' &&
      parsed.hostname === LEADSALES_STORAGE_BUCKET_HOST;
    if (!isBucketHost && !isLeadsalesStorageUrl(url)) return undefined;
    pathname = parsed.pathname;
  } catch {
    return undefined;
  }

  const start = pathname.indexOf(STORAGE_DRIVE_SEGMENT);
  if (start === -1) return undefined;
  const encoded = pathname.slice(start + STORAGE_DRIVE_SEGMENT.length);
  if (!encoded) return undefined;

  try {
    return decodeURIComponent(encoded);
  } catch {
    // A stray `%` that is not an escape: the path is already the id.
    return encoded;
  }
}

/**
 * Returns true when the input looks like a social media URL or @handle.
 * Parses the hostname to avoid false positives like "notfacebook.com".
 */
export function isSocialMediaUrl(value: string): boolean {
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return false;
  if (trimmed.startsWith('@')) return true;

  try {
    const url = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
    const hostname = new URL(url).hostname;
    return hostnameMatchesDomain(hostname, SOCIAL_MEDIA_DOMAINS);
  } catch {
    return false;
  }
}
